// Pure helpers for quoted evidence (contract v8, kanban 9eb5f005): how an
// excerpt names its source, which sources the page can open, and where a
// quote sits in a transcript. No DOM, no React.

import type { InitiativeEvidence, RoadmapSourceKind, SourceRef, SourceSegment } from '../../services/roadmapReports';
import { fmtDay, parseDay } from './roadmapTimelineUtils';

/** Sources the viewer can open; a document or knowledge-bank entry is named only. */
const OPENABLE: ReadonlySet<RoadmapSourceKind> = new Set(['meeting', 'notes', 'email_thread']);

export const SOURCE_KIND_WORDS: Record<RoadmapSourceKind, string> = {
  meeting: 'meeting',
  notes: 'notes',
  email_thread: 'email',
  document: 'document',
};

export function canOpenSource(kind: RoadmapSourceKind): boolean {
  return OPENABLE.has(kind);
}

/** The source ref an excerpt opens: the resolved full id when the server had
 * one, else the id as Merlin cited it (the endpoint accepts a uuid). Null for
 * a source the page cannot open. */
export function evidenceSourceRef(ev: InitiativeEvidence): SourceRef | null {
  if (!canOpenSource(ev.source_kind)) return null;
  return { kind: ev.source_kind, id: ev.resolved_id ?? ev.source_id, quote: ev.quote };
}

/** How an excerpt's source reads: "Alpha Team weekly, Sep 29" when the meeting
 * resolved; "meeting 14c6e0b3, Aug 27" from the cited id and stated day
 * otherwise; a knowledge-bank entry by its short id. */
export function evidenceSourceLabel(ev: InitiativeEvidence): string {
  const day = parseDay(ev.source_date ?? ev.on);
  const when = day ? `, ${fmtDay(day)}` : '';
  if (ev.label) return `${ev.label}${when}`;
  const id = ev.source_id.trim();
  if (ev.source_kind === 'document') {
    const kb = /^(?:kb:|(?:dev|business|code|ops)\s+KB\s*)([0-9a-f]{8})/i.exec(id);
    return kb ? `knowledge bank entry ${kb[1]}${when}` : `${id}${when}`;
  }
  const short = /^[0-9a-f-]{8,}$/i.test(id) ? id.slice(0, 8) : id;
  return `${SOURCE_KIND_WORDS[ev.source_kind]} ${short}${when}`;
}

/** "Hadi Tawil, Alpha Team weekly, Sep 29" — the attribution line under a quote. */
export function evidenceAttribution(ev: InitiativeEvidence): string {
  const where = evidenceSourceLabel(ev);
  return ev.said_by ? `${ev.said_by}, ${where}` : where;
}

/** The excerpts that name this question (exact text, whitespace folded). */
export function evidenceForQuestion(evidence: readonly InitiativeEvidence[] | undefined, question: string): InitiativeEvidence[] {
  const key = question.split(/\s+/).join(' ').trim();
  return (evidence ?? []).filter((e) => e.for_questions.some((q) => q.split(/\s+/).join(' ').trim() === key));
}

/** The excerpts Merlin attached to one sub-action (by its key). */
export function evidenceForAction(evidence: readonly InitiativeEvidence[] | undefined, actionKey: string): InitiativeEvidence[] {
  return (evidence ?? []).filter((e) => e.for_actions.includes(actionKey));
}

/** The excerpts that back the initiative itself (named no question and no sub-action). */
export function evidenceForInitiative(evidence: readonly InitiativeEvidence[] | undefined): InitiativeEvidence[] {
  return (evidence ?? []).filter((e) => e.for_questions.length === 0 && e.for_actions.length === 0);
}

/**
 * A legacy free-text citation ("meeting 14c6e0b3 2026-08-27", "email fff4eada
 * 2026-09-17", "recall meeting 8e309fda (AI check-in)") as a source the
 * viewer can open, or null when it names nothing openable (a KB entry, prose).
 */
export function sourceRefFromCitation(citation: string): SourceRef | null {
  const s = citation.trim();
  const meeting = /^(?:recall\s+)?(?:meeting|fireflies|plaud|recall)\s+([0-9a-f]{8,}(?:-[0-9a-f-]+)?)/i.exec(s);
  if (meeting) return { kind: 'meeting', id: meeting[1].toLowerCase() };
  const notes = /^(?:delta_force_notes|notes?)\s+(?:row\s+)?([0-9a-f]{8,}(?:-[0-9a-f-]+)?)/i.exec(s);
  if (notes) return { kind: 'notes', id: notes[1].toLowerCase() };
  const email = /^emails?\s+([0-9a-f]{8,}(?:-[0-9a-f-]+)?)/i.exec(s);
  if (email) return { kind: 'email_thread', id: email[1].toLowerCase() };
  return null;
}

/** The `?source=<kind>:<id>` deep link the server writes into `source_url`
 * (and the digest emails carry), read back on page load. */
export function parseSourceParam(search: string): SourceRef | null {
  // A hash may ride along when a caller hands over the whole href tail.
  const raw = new URLSearchParams(search.split('#')[0]).get('source');
  if (!raw) return null;
  const idx = raw.indexOf(':');
  if (idx <= 0) return null;
  const kind = raw.slice(0, idx) as RoadmapSourceKind;
  const id = raw.slice(idx + 1).trim();
  if (!canOpenSource(kind) || !id) return null;
  return { kind, id };
}

// ── Finding a quote in a transcript ──────────────────────────────────────────

const fold = (t: string) => t.toLowerCase().replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/\s+/g, ' ').trim();

/** Does this text contain the quote? Case, curly quotes and runs of
 * whitespace are ignored; when the whole quote is not there (a transcription
 * differs by a word), its first forty characters are enough. */
export function textHasQuote(text: string, quote: string | null | undefined): boolean {
  if (!quote) return false;
  const t = fold(text);
  const q = fold(quote);
  if (!q) return false;
  if (t.includes(q)) return true;
  const head = q.slice(0, 40);
  return head.length >= 12 && t.includes(head);
}

/** Index of the first segment that carries the quote, or -1. A quote that
 * spans two turns is found by its opening words. */
export function segmentIndexForQuote(segments: readonly SourceSegment[], quote: string | null | undefined): number {
  if (!quote) return -1;
  const full = segments.findIndex((s) => textHasQuote(s.text, quote));
  if (full !== -1) return full;
  const head = fold(quote).slice(0, 24);
  if (head.length < 12) return -1;
  return segments.findIndex((s) => fold(s.text).includes(head));
}

/** Split a transcript into paragraphs the viewer can mark, when there are no
 * segments: one per line, blank lines dropped. */
export function paragraphs(text: string): string[] {
  return text.split(/\n+/).map((p) => p.trim()).filter(Boolean);
}

/** The lines (paragraph indexes) that match a find-box term, case-insensitive. */
export function matchingIndexes(lines: readonly string[], term: string): number[] {
  const t = fold(term);
  if (t.length < 2) return [];
  return lines.map((l, i) => (fold(l).includes(t) ? i : -1)).filter((i) => i !== -1);
}

/** mm:ss for a segment's start, "" when unknown. */
export function fmtClock(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
